import { Navigate, Outlet, useLocation, type RouteObject } from "react-router";
import { useAuth } from "./app/auth";
import { ClubGate } from "./app/club";
import { moduleRoutes } from "./app/registry";
import { Shell } from "./app/Shell";
import { ChooseClubPage, NoClubPage } from "./app/pages/ChooseClub";
import { HomePage } from "./app/pages/Home";
import { NotFoundPage, RouteError } from "./app/pages/Misc";
import { ResetPasswordPage } from "./app/pages/ResetPassword";
import { SignInPage } from "./app/pages/SignIn";
import { modules } from "./modules";
import type { ModuleDefinition } from "./modules/types";
import { ErrorState, Loading } from "./ui/feedback";

function LoadingScreen() {
  return (
    <div className="cc-center">
      <Loading />
    </div>
  );
}

function RequireAuth() {
  const { state, retry } = useAuth();
  const location = useLocation();
  if (state.status === "loading") return <LoadingScreen />;
  if (state.status === "error") return <div className="cc-center"><ErrorState error={state.error} onRetry={retry} /></div>;
  if (state.status === "signedOut") {
    // Signing out on purpose leaves no return path behind: the next person to sign in on this browser starts at Home.
    const from = state.explicit ? undefined : location.pathname + location.search + location.hash;
    return <Navigate to="/sign-in" replace state={{ from, expired: state.expired, userId: state.previousUserId }} />;
  }
  return <Outlet />;
}

function ClubLayout() {
  return (
    <ClubGate>
      <Shell />
    </ClubGate>
  );
}

export function buildRoutes(moduleList: readonly ModuleDefinition[] = modules): RouteObject[] {
  return [
    { path: "/sign-in", element: <SignInPage />, HydrateFallback: LoadingScreen },
    { path: "/microsoft-complete", lazy: async () => ({ Component: (await import("./app/pages/MicrosoftComplete")).default }), HydrateFallback: LoadingScreen },
    { path: "/sitekings-complete", lazy: async () => ({ Component: (await import("./app/pages/SiteKingsComplete")).default }), HydrateFallback: LoadingScreen },
    { path: "/reset-password", element: <ResetPasswordPage />, HydrateFallback: LoadingScreen },
    // The shared housekeeping tablet: outside the staff sign-in, with its own code for /lodging/tablet/* only.
    {
      path: "/housekeeping-tablet",
      HydrateFallback: LoadingScreen,
      lazy: async () => ({ Component: (await import("./modules/lodging/tablet/TabletPage")).default }),
    },
    {
      element: <RequireAuth />,
      HydrateFallback: LoadingScreen,
      errorElement: <RouteError />,
      children: [
        { path: "/pos-exit-approval", lazy: async () => ({ Component: (await import("./modules/pos/ExitApprovalPage")).default }) },
        { path: "/choose-club", element: <ChooseClubPage /> },
        { path: "/no-club", element: <NoClubPage /> },
        {
          element: <ClubLayout />,
          errorElement: <RouteError />,
          children: [{ index: true, element: <HomePage /> }, { path: "settings", lazy: async () => ({ Component: (await import("./app/pages/Settings")).default }) }, ...moduleRoutes(moduleList), { path: "*", element: <NotFoundPage /> }],
        },
      ],
    },
  ];
}

